import { IonContent, IonPage } from '@ionic/react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Ball,
  ChanceyCard,
  Chip,
  FreqBar,
  GameBadge,
  Icon,
  Sparkline,
} from '../components/chancey';
import { safeAreaPadding } from '../components/layout/safeArea';
import { ApiError } from '../api/client';
import { useGameApi } from '../api/games';
import type { FrequencyStatsResponse, PairingsStatsResponse } from '../api/types';
import { JACKPOT_TREND } from '../data/lotteryData';
import { useActiveGame } from '../hooks/useActiveGame';
import { useUserPrefs } from '../hooks/useUserPrefs';
import { useChanceyTheme } from '../theme/ThemeContext';
import { StatsSimpleView } from './stats/StatsSimpleView';

function StatsAdvancedView(): JSX.Element {
  const { c, typ, d } = useChanceyTheme();
  const { game } = useActiveGame();
  const { getFrequencyStats, getPairingsStats } = useGameApi();
  const [tab, setTab] = useState<'freq' | 'pairs' | 'trends'>('freq');
  const [range, setRange] = useState('100');
  const windowN = useMemo(() => {
    const n = Number(range);
    return Number.isFinite(n) && Number.isInteger(n) ? n : 100;
  }, [range]);

  const [freqStats, setFreqStats] = useState<FrequencyStatsResponse | null>(null);
  const [freqLoading, setFreqLoading] = useState(true);
  const [freqError, setFreqError] = useState<string | null>(null);

  const [pairStats, setPairStats] = useState<PairingsStatsResponse | null>(null);
  const [pairLoading, setPairLoading] = useState(false);
  const [pairError, setPairError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setFreqLoading(true);
    setFreqError(null);
    getFrequencyStats(game.id, windowN)
      .then((res) => {
        if (cancelled) return;
        setFreqStats(res);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          console.error('[Chancey stats] frequency API error', {
            status: e.status,
            code: e.code,
            message: e.message,
          });
        } else {
          console.error('[Chancey stats] frequency request failed', e);
        }
        setFreqStats(null);
        setFreqError(e instanceof Error ? e.message : 'Could not load stats');
      })
      .finally(() => {
        if (cancelled) return;
        setFreqLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [game.id, getFrequencyStats, windowN]);

  useEffect(() => {
    // Pairings are only needed when the tab is visible; keep last data to avoid flicker.
    if (tab !== 'pairs') return;
    let cancelled = false;
    setPairLoading(true);
    setPairError(null);
    getPairingsStats(game.id, windowN)
      .then((res) => {
        if (cancelled) return;
        setPairStats(res);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          console.error('[Chancey stats] pairings API error', {
            status: e.status,
            code: e.code,
            message: e.message,
          });
        } else {
          console.error('[Chancey stats] pairings request failed', e);
        }
        setPairStats(null);
        setPairError(e instanceof Error ? e.message : 'Could not load pairings');
      })
      .finally(() => {
        if (cancelled) return;
        setPairLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [game.id, getPairingsStats, tab, windowN]);

  const hasFreqData = freqStats !== null;
  const frequency = hasFreqData ? freqStats.frequency : [];
  const freqMax = frequency.length > 0 ? Math.max(1, ...frequency.map((f) => f.count)) : 1;
  const hot = hasFreqData ? freqStats.hot : [];
  const cold = hasFreqData ? freqStats.cold : [];
  const kpis = hasFreqData ? freqStats.kpis : null;
  const overdueList = hasFreqData ? freqStats.overdue : [];

  const hasPairData = pairStats !== null && !pairError;
  const oddEvenDist = hasPairData ? pairStats.oddEvenDistribution : [];
  const sumRange = hasPairData ? pairStats.sumRange : null;

  const pairRowsForUi = useMemo(() => {
    if (!pairStats) return [];
    return pairStats.pairs.filter(
      (p) =>
        Number.isFinite(p.a) &&
        Number.isFinite(p.b) &&
        Number.isFinite(p.count) &&
        Number.isFinite(p.lift)
    );
  }, [pairStats]);

  const showSkeleton = freqLoading && !freqStats;
  /** Loaded at least once for this request cycle; no mock fallback — show blank placeholders. */
  const showFreqBlank = !freqLoading && !hasFreqData;
  /** While refetching, old pairStats still holds the previous window — avoid showing stale odd/even + sum range. */
  const showPairSkeleton =
    pairLoading && (!pairStats || pairStats.window !== windowN);
  /** Pairings API failed or not loaded — no mock charts. */
  const showPairSectionBlank = tab === 'pairs' && !showPairSkeleton && !hasPairData;
  const shimmerStyle: React.CSSProperties = useMemo(
    () => ({
      background: `linear-gradient(90deg, ${c.surfaceAlt} 0%, ${c.surface} 50%, ${c.surfaceAlt} 100%)`,
      backgroundSize: '200% 100%',
      animation: 'chanceyShimmer 1.2s ease-in-out infinite',
    }),
    [c.surface, c.surfaceAlt]
  );

  return (
    <>
      <style>{`
        @keyframes chanceyShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
      <div
        className="flex flex-col"
        style={{ ...safeAreaPadding(8, d.pad, 16, d.pad), gap: d.gap, color: c.ink }}
      >
          <div className="flex flex-col items-start gap-1.5 px-0.5 pb-0.5 pt-1">
            <GameBadge game={game} />
            <div
              className="text-[32px] leading-tight"
              style={{
                fontFamily: typ.display,
                fontWeight: typ.displayWeight,
                letterSpacing: typ.displayLetter,
              }}
            >
              Statistics
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <div
              className="text-[10px] font-medium uppercase tracking-[0.08em]"
              style={{ color: c.inkMuted, fontFamily: typ.body }}
            >
              Recent draws
            </div>
            <div className="hide-scrollbar flex gap-1.5 overflow-x-auto">
              {[
                { v: '25', l: '25' },
                { v: '50', l: '50' },
                { v: '100', l: '100' },
                { v: '200', l: '200' },
                { v: '500', l: '500' },
              ].map((r) => (
                <Chip key={r.v} c={c} typ={typ} active={range === r.v} onClick={() => setRange(r.v)}>
                  {r.l}
                </Chip>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <ChanceyCard c={c} pad={12}>
              <div
                className="text-[10px] font-medium uppercase tracking-[0.08em]"
                style={{ color: c.inkMuted, fontFamily: typ.body }}
              >
                Hot streak
              </div>
              <div className="mt-1 flex items-center gap-1.5">
                {showSkeleton ? (
                  <>
                    <div className="h-[26px] w-[26px] rounded-full" style={shimmerStyle} />
                    <div className="h-[18px] w-10 rounded" style={shimmerStyle} />
                  </>
                ) : showFreqBlank || !kpis ? (
                  <span className="font-mono text-[18px] font-semibold" style={{ fontFamily: typ.mono, color: c.inkMuted }}>
                    —
                  </span>
                ) : (
                  <>
                    <Ball n={kpis.hotStreak.n} size={26} c={c} />
                    <div className="flex items-baseline gap-1">
                      <div className="font-mono text-[18px] font-semibold" style={{ fontFamily: typ.mono, color: c.ink }}>
                        {kpis.hotStreak.streak}
                      </div>
                      <div className="text-[10px]" style={{ fontFamily: typ.body, color: c.inkMuted }}>
                        in a row
                      </div>
                    </div>
                  </>
                )}
              </div>
            </ChanceyCard>
            <ChanceyCard c={c} pad={12}>
              <div
                className="text-[10px] font-medium uppercase tracking-[0.08em]"
                style={{ color: c.inkMuted, fontFamily: typ.body }}
              >
                Most overdue
              </div>
              <div className="mt-1 flex items-center gap-1.5">
                {showSkeleton ? (
                  <>
                    <div className="h-[26px] w-[26px] rounded-full" style={shimmerStyle} />
                    <div className="h-[18px] w-12 rounded" style={shimmerStyle} />
                  </>
                ) : showFreqBlank || !kpis ? (
                  <span className="font-mono text-[18px] font-semibold" style={{ fontFamily: typ.mono, color: c.inkMuted }}>
                    —
                  </span>
                ) : (
                  <>
                    <Ball n={kpis.mostOverdue.n} size={26} c={c} />
                    <div className="flex items-baseline gap-1">
                      <div className="font-mono text-[18px] font-semibold" style={{ fontFamily: typ.mono, color: c.ink }}>
                        {kpis.mostOverdue.days}
                      </div>
                      <div className="text-[10px]" style={{ fontFamily: typ.body, color: c.inkMuted }}>
                        ago
                      </div>
                    </div>
                  </>
                )}
              </div>
            </ChanceyCard>
            <ChanceyCard c={c} pad={12}>
              <div
                className="text-[10px] font-medium uppercase tracking-[0.08em]"
                style={{ color: c.inkMuted, fontFamily: typ.body }}
              >
                Odd/Even
              </div>
              {showSkeleton ? (
                <div className="mt-1 h-[22px] w-12 rounded" style={shimmerStyle} />
              ) : showFreqBlank || !kpis ? (
                <div className="mt-1 font-mono text-[22px] font-semibold" style={{ fontFamily: typ.mono, color: c.inkMuted }}>
                  —
                </div>
              ) : (
                <div className="mt-1 font-mono text-[22px] font-semibold" style={{ fontFamily: typ.mono, color: c.ink }}>
                  {kpis.oddEvenMode.ratio}
                </div>
              )}
            </ChanceyCard>
          </div>

          {freqError ? (
            <div className="text-[12px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
              {freqError}
            </div>
          ) : null}

          <div
            className="flex rounded-xl p-[3px]"
            style={{ background: c.surfaceAlt, borderRadius: 12 }}
          >
            {[
              { v: 'freq' as const, l: 'Frequency' },
              { v: 'pairs' as const, l: 'Pairings' },
            ].map((t) => (
              <button
                key={t.v}
                type="button"
                className="flex-1 rounded-[10px] py-2.5 text-center text-[13px] font-semibold transition-all"
                style={{
                  background: tab === t.v ? c.surface : 'transparent',
                  color: tab === t.v ? c.ink : c.inkSoft,
                  boxShadow: tab === t.v ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                  fontFamily: typ.body,
                }}
                onClick={() => setTab(t.v)}
              >
                {t.l}
              </button>
            ))}
          </div>

          {tab === 'freq' && (
            <>
              <div className="grid grid-cols-2 gap-2.5">
                <ChanceyCard c={c} pad={14}>
                  <div className="mb-2.5 flex items-center gap-1.5">
                    {Icon.flame(16, c.accent)}
                    <div
                      className="text-xs font-semibold uppercase tracking-[0.06em]"
                      style={{ fontFamily: typ.body, color: c.ink }}
                    >
                      Hot
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {showSkeleton
                      ? Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="h-[34px] w-[34px] rounded-full" style={shimmerStyle} />
                        ))
                      : showFreqBlank
                        ? (
                            <span className="font-mono text-[15px] font-semibold" style={{ fontFamily: typ.mono, color: c.inkMuted }}>
                              —
                            </span>
                          )
                        : hot.map((f) => <Ball key={f.n} n={f.n} size={34} c={c} />)}
                  </div>
                </ChanceyCard>
                <ChanceyCard c={c} pad={14}>
                  <div className="mb-2.5 flex items-center gap-1.5">
                    {Icon.ice(16, c.accent3)}
                    <div
                      className="text-xs font-semibold uppercase tracking-[0.06em]"
                      style={{ fontFamily: typ.body, color: c.ink }}
                    >
                      Cold
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {showSkeleton
                      ? Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="h-[34px] w-[34px] rounded-full" style={shimmerStyle} />
                        ))
                      : showFreqBlank
                        ? (
                            <span className="font-mono text-[15px] font-semibold" style={{ fontFamily: typ.mono, color: c.inkMuted }}>
                              —
                            </span>
                          )
                        : cold.map((f) => <Ball key={f.n} n={f.n} size={34} muted c={c} />)}
                  </div>
                </ChanceyCard>
              </div>

              <ChanceyCard c={c} pad={14}>
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-[13px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
                    Full heatmap
                  </div>
                  <div className="text-[11px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                    tap any number
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {showSkeleton
                    ? Array.from({ length: game.mainRange }).map((_, i) => (
                        <div key={i} className="flex flex-col items-center gap-0.5">
                          <div className="h-[34px] w-[34px] rounded-full" style={shimmerStyle} />
                          <div className="h-3 w-5 rounded" style={shimmerStyle} />
                        </div>
                      ))
                    : showFreqBlank ? (
                        <div
                          className="col-span-7 py-6 text-center font-mono text-[15px] font-semibold"
                          style={{ fontFamily: typ.mono, color: c.inkMuted }}
                        >
                          —
                        </div>
                      )
                    : frequency.map((f) => {
                        const pct = f.count / freqMax;
                        const isHot = hot.some((h) => h.n === f.n);
                        const isCold = cold.some((co) => co.n === f.n);
                        return (
                          <div key={f.n} className="flex flex-col items-center gap-0.5">
                            <div
                              className="relative flex h-[34px] w-[34px] items-center justify-center rounded-full font-mono text-[13px] font-semibold"
                              style={{
                                background: isHot ? c.accent : isCold ? c.surfaceAlt : c.ink,
                                color: isCold ? c.inkSoft : isHot ? c.accentInk : c.bg,
                                opacity: isCold ? 0.55 + pct * 0.4 : 0.5 + pct * 0.5,
                                fontFamily: typ.mono,
                              }}
                            >
                              {f.n}
                              <div
                                className="pointer-events-none absolute inset-0 rounded-full"
                                style={{
                                  background:
                                    'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.3) 0%, transparent 60%)',
                                }}
                              />
                            </div>
                            <div className="font-mono text-[9px]" style={{ color: c.inkMuted, fontFamily: typ.mono }}>
                              {f.count}
                            </div>
                          </div>
                        );
                      })}
                </div>
              </ChanceyCard>

              <ChanceyCard c={c} pad={14}>
                <div className="mb-3 text-[13px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
                  Top 10 ranked
                </div>
                <div className="flex flex-col gap-[7px]">
                  {showSkeleton
                    ? Array.from({ length: 10 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          <div className="h-4 w-[22px] rounded" style={shimmerStyle} />
                          <div className="h-2 flex-1 rounded" style={shimmerStyle} />
                          <div className="h-4 w-6 rounded" style={shimmerStyle} />
                        </div>
                      ))
                    : showFreqBlank ? (
                        <div className="font-mono text-[15px] font-semibold" style={{ fontFamily: typ.mono, color: c.inkMuted }}>
                          —
                        </div>
                      )
                    : [...frequency]
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 10)
                        .map((f, i) => (
                          <FreqBar key={f.n} n={f.n} count={f.count} max={freqMax} c={c} highlight={i < 3} />
                        ))}
                </div>
              </ChanceyCard>

              <ChanceyCard c={c} pad={14}>
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-[13px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
                    Overdue
                  </div>
                  <div className="text-[11px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                    days since last draw
                  </div>
                </div>
                <div className="flex flex-col gap-2.5">
                  {showSkeleton
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full" style={shimmerStyle} />
                          <div className="h-2 flex-1 rounded" style={shimmerStyle} />
                          <div className="h-4 w-16 rounded" style={shimmerStyle} />
                        </div>
                      ))
                    : showFreqBlank ? (
                        <div className="font-mono text-[15px] font-semibold" style={{ fontFamily: typ.mono, color: c.inkMuted }}>
                          —
                        </div>
                      )
                    : overdueList.map((o) => (
                        <div key={o.n} className="flex items-center gap-3">
                          <Ball n={o.n} size={32} c={c} />
                          <div className="relative h-1 flex-1 overflow-hidden rounded-sm" style={{ background: c.surfaceAlt }}>
                            <div
                              className="h-full rounded-sm"
                              style={{
                                width: `${Math.min(100, (o.days / Math.max(1, overdueList[0]?.days ?? 1)) * 100)}%`,
                                background: c.accent2,
                              }}
                            />
                          </div>
                          <div
                            className="min-w-[50px] text-right font-mono text-[13px] font-semibold"
                            style={{ fontFamily: typ.mono, color: c.ink }}
                          >
                            {o.days} days
                          </div>
                        </div>
                      ))}
                </div>
              </ChanceyCard>
            </>
          )}

          {tab === 'pairs' && (
            <>
              <ChanceyCard c={c} pad={14}>
                <div className="mb-1 text-[13px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
                  Strongest pairs
                </div>
                <div className="mb-3.5 text-[11px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                  Numbers that co-occur more than chance predicts (lift &gt; 1.0)
                </div>
                <div className="flex flex-col gap-3.5">
                  {pairError ? (
                    <div className="text-[12px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                      {pairError}
                    </div>
                  ) : showPairSkeleton ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="flex gap-1.5">
                          <div className="h-[34px] w-[34px] rounded-full" style={shimmerStyle} />
                          <div className="h-[34px] w-[34px] rounded-full" style={shimmerStyle} />
                        </div>
                        <div className="flex-1">
                          <div className="h-4 w-28 rounded" style={shimmerStyle} />
                          <div className="mt-1 h-3 w-32 rounded" style={shimmerStyle} />
                        </div>
                        <div className="h-[28px] w-[70px] rounded" style={shimmerStyle} />
                      </div>
                    ))
                  ) : pairRowsForUi.length === 0 && pairStats !== null ? (
                    <div className="text-[12px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                      No pairs met the lift filter for this draw window.
                    </div>
                  ) : pairRowsForUi.length === 0 ? (
                    <div className="font-mono text-[15px] font-semibold" style={{ fontFamily: typ.mono, color: c.inkMuted }}>
                      —
                    </div>
                  ) : (
                    pairRowsForUi.map((p, i) => (
                      <div key={`${p.a}-${p.b}-${i}`} className="flex items-center gap-3">
                        <div className="flex gap-1.5">
                          <Ball n={p.a} size={34} c={c} />
                          <Ball n={p.b} size={34} c={c} />
                        </div>
                        <div className="flex-1">
                          <div
                            className="font-mono text-[13px] font-semibold"
                            style={{ color: c.ink, fontFamily: typ.mono }}
                          >
                            ×{p.lift.toFixed(1)} lift
                          </div>
                          <div className="text-[11px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                            {p.count} co-occurrences
                          </div>
                        </div>
                        <Sparkline
                          data={[2, 3, 4, 3, 5, 4, 6, 5, 7, 6, 8, 7]}
                          w={70}
                          h={28}
                          color={c.accent}
                          fill={c.accent}
                        />
                      </div>
                    ))
                  )}
                </div>
              </ChanceyCard>

              <ChanceyCard c={c} pad={14}>
                <div className="mb-3 text-[13px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
                  Odd / Even distribution
                </div>
                <div className="flex h-[100px] items-end gap-2">
                  {showPairSkeleton
                    ? Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="flex h-full flex-1 flex-col items-center gap-1.5">
                          <div className="flex w-full flex-1 items-end">
                            <div className="w-full rounded" style={{ height: `${30 + i * 8}%`, ...shimmerStyle }} />
                          </div>
                          <div className="h-3 w-8 rounded" style={shimmerStyle} />
                        </div>
                      ))
                    : showPairSectionBlank ? (
                        <div
                          className="flex w-full items-center justify-center font-mono text-[15px] font-semibold"
                          style={{ fontFamily: typ.mono, color: c.inkMuted }}
                        >
                          —
                        </div>
                      )
                    : (() => {
                        const max = Math.max(1, ...oddEvenDist.map((b) => b.count));
                        return oddEvenDist.map((b, i) => (
                          <div key={b.ratio} className="flex h-full flex-1 flex-col items-center gap-1.5">
                            <div className="flex w-full flex-1 items-end">
                              <div
                                className="w-full rounded"
                                style={{
                                  height: `${(b.count / max) * 100}%`,
                                  background: i === 2 || i === 3 ? c.accent : c.ink,
                                  opacity: i === 2 || i === 3 ? 1 : 0.6,
                                }}
                              />
                            </div>
                            <div className="font-mono text-[10px]" style={{ color: c.inkSoft, fontFamily: typ.mono }}>
                              {b.ratio}
                            </div>
                          </div>
                        ));
                      })()}
                </div>
              </ChanceyCard>

              <ChanceyCard c={c} pad={14}>
                <div className="mb-1 text-[13px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
                  Winning sum ranges
                </div>
                {showPairSectionBlank || !sumRange ? (
                  <>
                    <div className="mb-3.5 text-[11px] font-mono font-semibold" style={{ color: c.inkMuted, fontFamily: typ.mono }}>
                      —
                    </div>
                    <div className="relative h-11">
                      <div className="absolute inset-x-0 top-4 bottom-4 rounded-md" style={{ background: c.surfaceAlt }} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3.5 text-[11px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                      {Math.round(sumRange.pct * 100)}% of wins sum between {sumRange.lo} and {sumRange.hi}
                    </div>
                    <div className="relative h-11">
                      <div className="absolute inset-x-0 top-4 bottom-4 rounded-md" style={{ background: c.surfaceAlt }} />
                      <div
                        className="absolute top-3.5 bottom-3.5 rounded-md opacity-90"
                        style={{
                          left: `${((sumRange.lo - sumRange.minPossible) / Math.max(1, sumRange.maxPossible - sumRange.minPossible)) * 100}%`,
                          right: `${((sumRange.maxPossible - sumRange.hi) / Math.max(1, sumRange.maxPossible - sumRange.minPossible)) * 100}%`,
                          background: c.accent,
                        }}
                      />
                      <div
                        className="absolute bottom-[-4px] left-0 font-mono text-[10px]"
                        style={{ color: c.inkMuted, fontFamily: typ.mono }}
                      >
                        {sumRange.minPossible}
                      </div>
                      <div
                        className="absolute bottom-[-4px] font-mono text-[10px] font-semibold"
                        style={{
                          left: `${((sumRange.lo - sumRange.minPossible) / Math.max(1, sumRange.maxPossible - sumRange.minPossible)) * 100}%`,
                          transform: 'translateX(-50%)',
                          color: c.ink,
                          fontFamily: typ.mono,
                        }}
                      >
                        {sumRange.lo}
                      </div>
                      <div
                        className="absolute bottom-[-4px] font-mono text-[10px] font-semibold"
                        style={{
                          left: `${((sumRange.hi - sumRange.minPossible) / Math.max(1, sumRange.maxPossible - sumRange.minPossible)) * 100}%`,
                          transform: 'translateX(-50%)',
                          color: c.ink,
                          fontFamily: typ.mono,
                        }}
                      >
                        {sumRange.hi}
                      </div>
                      <div
                        className="absolute bottom-[-4px] right-0 font-mono text-[10px]"
                        style={{ color: c.inkMuted, fontFamily: typ.mono }}
                      >
                        {sumRange.maxPossible}
                      </div>
                    </div>
                  </>
                )}
              </ChanceyCard>
            </>
          )}

          {tab === 'trends' && (
            <>
              <ChanceyCard c={c} pad={14}>
                <div className="mb-3.5 flex items-baseline justify-between">
                  <div>
                    <div className="text-[13px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
                      Jackpot trend
                    </div>
                    <div className="mt-0.5 text-[11px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                      Last 10 rollovers
                    </div>
                  </div>
                  <div className="font-mono text-xl font-semibold" style={{ color: c.win, fontFamily: typ.mono }}>
                    +194%
                  </div>
                </div>
                <Sparkline data={JACKPOT_TREND} w={340} h={110} color={c.accent} fill={c.accent} strokeW={2.5} />
                <div
                  className="mt-1.5 flex justify-between font-mono text-[10px]"
                  style={{ color: c.inkMuted, fontFamily: typ.mono }}
                >
                  <span>Jan</span>
                  <span>Feb</span>
                  <span>Mar</span>
                  <span>Apr</span>
                </div>
              </ChanceyCard>

              <ChanceyCard c={c} pad={14}>
                <div className="mb-1 text-[13px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
                  Prediction confidence
                </div>
                <div className="mb-3.5 text-[11px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                  Where the current pattern signals a win
                </div>
                <div className="relative flex h-[120px] items-center justify-center">
                  <svg width="120" height="120" viewBox="0 0 120 120" aria-hidden>
                    <circle cx="60" cy="60" r="48" fill="none" stroke={c.surfaceAlt} strokeWidth="10" />
                    <circle
                      cx="60"
                      cy="60"
                      r="48"
                      fill="none"
                      stroke={c.accent}
                      strokeWidth="10"
                      strokeDasharray={`${0.74 * 301.6} 301.6`}
                      strokeLinecap="round"
                      transform="rotate(-90 60 60)"
                    />
                  </svg>
                  <div className="absolute text-center">
                    <div
                      className="text-4xl leading-none"
                      style={{
                        fontFamily: typ.display,
                        fontWeight: typ.displayWeight,
                        letterSpacing: typ.displayLetter,
                        color: c.ink,
                      }}
                    >
                      74%
                    </div>
                    <div className="mt-0.5 text-[11px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                      signal strength
                    </div>
                  </div>
                </div>
                <div
                  className="mt-2 flex gap-2 rounded-[10px] p-2.5"
                  style={{ background: c.surfaceAlt }}
                >
                  {Icon.sparkle(14, c.accent)}
                  <div className="text-xs leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                    Past performance doesn&apos;t predict future draws. Use these signals for fun, not strategy.
                  </div>
                </div>
              </ChanceyCard>
            </>
          )}
      </div>
    </>
  );
}

const StatsPage: React.FC = () => {
  const { prefs } = useUserPrefs();
  const { c } = useChanceyTheme();
  return (
    <IonPage>
      <IonContent fullscreen style={{ '--background': c.bg }}>
        {prefs.experienceMode === 'simple' ? <StatsSimpleView /> : <StatsAdvancedView />}
      </IonContent>
    </IonPage>
  );
};

export default StatsPage;
